import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { auth, type AuthUser } from '../../lib/api';

const SESSION_STORAGE_KEY = 'fc_session_token';
const IS_LOCAL_DEV = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const LOCAL_DEV_USER: AuthUser = {
  id: 'local-dev-admin',
  email: 'dev@fastingcarnivore.local',
  name: 'Local Dev Admin',
  isAdmin: true,
  hasPurchased: true,
};

interface User {
  id: string;
  email: string;
  name?: string;
  isAdmin: boolean;
  hasPurchased: boolean;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasPurchased: boolean;
  sessionToken: string | null;
  logout: () => Promise<void>;
  setSessionToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isAdmin: false,
  hasPurchased: false,
  sessionToken: null,
  logout: async () => {},
  setSessionToken: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [sessionToken, setSessionTokenState] = useState<string | null>(() => {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  });
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch current user from REST API
  useEffect(() => {
    // On localhost, always call /me — the worker auto-auths as admin even without a token
    if (!sessionToken && !IS_LOCAL_DEV) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    auth.me()
      .then((meResponse) => {
        if (cancelled) return;
        const wrapped = meResponse as unknown as { user?: AuthUser } | null;
        const userData = wrapped && 'user' in wrapped ? wrapped.user : (meResponse as AuthUser | null);
        if (userData) {
          setUser(userData);
        } else if (IS_LOCAL_DEV) {
          setUser(LOCAL_DEV_USER);
        } else if (!IS_LOCAL_DEV) {
          // Invalid session (only clear on production, not localhost)
          localStorage.removeItem(SESSION_STORAGE_KEY);
          setSessionTokenState(null);
          setUser(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (!IS_LOCAL_DEV) {
          localStorage.removeItem(SESSION_STORAGE_KEY);
          setSessionTokenState(null);
          setUser(null);
        } else {
          setUser(LOCAL_DEV_USER);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [sessionToken]);

  const setSessionToken = useCallback((token: string) => {
    localStorage.setItem(SESSION_STORAGE_KEY, token);
    setSessionTokenState(token);
  }, []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      // Clear locally even if server logout fails
    }
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setSessionTokenState(null);
    setUser(null);
  }, []);

  const contextValue: AuthContextType = {
    user: user ? {
      id: user.id,
      email: user.email,
      name: user.name ?? undefined,
      isAdmin: user.isAdmin,
      hasPurchased: user.hasPurchased,
    } : null,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    hasPurchased: user?.hasPurchased || false,
    sessionToken,
    logout,
    setSessionToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
